import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, getFilterDates, onCatchError, ReportService, Retorno, Route, StatusRetorno } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import HpmsCustomDialogAPI from '@philips-emr/hpms-stipulator-components/lib/api/hpms-custom-dialog-api';
import { HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { FetchAPI, FilterAPI, Stamp, StampIcon, TextAPI } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnBeforeFetch, OnClear, OnClick, OnDynamicContentRender, OnMount, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { ClearEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HistoryService, HttpService, msg } from '@philips-emr/tws-core/lib/services';
import { applicationContext } from '@philips-emr/tws-foundation';

import { ExclusaoBeneficiarioConsultaAliases as Aliases } from '../../codegen';
import { ParametroBeneficiario } from '../../interfaces/parametro-beneficiario';
import BaseController from '../BaseController';
import './exclusao-beneficiario-consulta-controller.scss';

interface Data {
  nrSeqRescisao: number;
  nrSeqRescisaoBenef: number;
  ieStatus: number;
  ieSolicitacaoRescisao: IeSolicitacaoRescisao;
}

export enum IeSolicitacaoRescisao {
  S = 'S',
  N = 'N'
}

const OptionKey = {
  DETALHE: 'detalhe',
  LIBERAR: 'liberar',
  CANCELAR: 'cancelar',
  REENVIAR_EXCLUSAO: 'reenviarExclusao',
  IMPRIMIR: 'imprimir',
};

function getStampExclusaoBeneficiario(ieStatus: number): ReactElement | null {
  let stampProps = null;

  switch (ieStatus) {
    case 1:
      stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label: '@status.exclusao-beneficiario/1' };
      break;
    case 2:
      stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label: '@status.exclusao-beneficiario/2' };
      break;
    case 3:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label: '@status.exclusao-beneficiario/3' };
      break;
    case 4:
      stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.CANCELED_OUTLINE, label: '@status.exclusao-beneficiario/4' };
      break;
    case 5:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label: '@status.exclusao-beneficiario/5' };
      break;
    case 6:
      stampProps = {
        icon: StampIcon.CANCELED_OUTLINE,
        label: '@status.exclusao-beneficiario/6',
        className: 'c-stamp-custom-color__exclusao-beneficiario-status-cancelado',
      };
      break;
    default:
      return null;
  }

  return <Stamp {...stampProps} />;
}

@Controller({ fragment: 'exclusao-beneficiario-consulta' })
export default class ExclusaoBeneficiarioConsultaController extends BaseController {

  @Inject('fetch-consulta')
  private fetchConsulta: FetchAPI;

  @Inject('hpms-custom-dialog')
  private customDialog: HpmsCustomDialogAPI;

  @Inject('text-protocolo')
  private textProtocolo: TextAPI;

  private ieLiberarRescisao: ParametroBeneficiario | undefined;

  private reportService: ReportService;

  @OnMount('panel-exclusao-beneficiario')
  public onMount(): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqContrato });

    void HttpService.get<ParametroBeneficiario>(Endpoint.GET.PARAMETRO_BENEFICIARIO, { nrSeqContrato })
      .then(response => {
        this.ieLiberarRescisao = response ?.body;
        this.fetchConsulta.forceFetch();
      });

    applicationContext.openModule('exclusao-beneficiario');

  }

  @OnDynamicContentRender('hpms-custom-card')
  public onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus } = data as Data;
    const stampExclusao = getStampExclusaoBeneficiario(ieStatus);

    if (stampExclusao) {
      render((): ReactElement => stampExclusao);
    }
  }

  @OnMount(Aliases.FILTER_CONSULTA)
  onFilterMount(event: MountEvent): void {
    FilterService.restoreFilterState(event);
  }

  @OnUnmount(Aliases.FILTER_CONSULTA)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }
  
  @OnBeforeFetch('fetch-consulta')
  public onBeforeFetchConsulta(event: FetchEvent): void {
    const api = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && api.setParameters({ ...api.getParameters(), nrSeqSubestipulante });
  }

  @OnClear('filter-consulta')
  public onClearFilter(event: ClearEvent): void {
    const api = event.target as unknown as FilterAPI;
    api.setData({});
    api.setData(getFilterDates('dtInicio', 'dtFim', 3));
  }

  @HPMSOnCreateOption('hpms-custom-card')
  public onCreateOption(event: HpmsCustomCardMenuRenderEvent<Data>): void {
    const { data, key, shouldRender } = event;
    const { ieStatus, ieSolicitacaoRescisao } = data;
    const isSolicitacaoRescisao = IeSolicitacaoRescisao.S === ieSolicitacaoRescisao;

    const show = {
      [OptionKey.DETALHE]: true,
      [OptionKey.LIBERAR]: ieStatus === 1 && this.ieLiberarRescisao ?.ieLiberarRescisao === 'N' && isSolicitacaoRescisao,
      [OptionKey.CANCELAR]: ieStatus === 1 && isSolicitacaoRescisao,
      [OptionKey.IMPRIMIR]: true,
      [OptionKey.REENVIAR_EXCLUSAO]: ieStatus === 5 && isSolicitacaoRescisao,
    };

    shouldRender(show[key]);
  }

  @OnClick('action-nova-exclusao')
  public onaAtionNovaExclusaoClick(): void {
    this.historyService.push(Route.BENEF_NOVA_EXCLUSAO);
  }

  @HPMSOnOptionClick('hpms-custom-card')
  public onOptionClick(event: HpmsCustomCardEvent<Data>): void {
    const { data, key } = event;
    const { nrSeqRescisao, ieSolicitacaoRescisao } = data;

    const action = {
      [OptionKey.DETALHE]: () => this.getRescisao(nrSeqRescisao, ieSolicitacaoRescisao),
      [OptionKey.REENVIAR_EXCLUSAO]: () => this.getReenvioRescisao(nrSeqRescisao, ieSolicitacaoRescisao),
      [OptionKey.LIBERAR]: () => this.liberar(nrSeqRescisao),
      [OptionKey.CANCELAR]: () => this.cancelar(nrSeqRescisao),
      [OptionKey.IMPRIMIR]: () => this.imprimir(nrSeqRescisao, ieSolicitacaoRescisao),

    };

    if (action[key]) {
      action[key]();
    }
  }

  private liberar(nrSeqRescisao: number): void {
    const { nrSeqContrato } = StipulatorContext.getCurrentContract();

    HttpService.put<Retorno<string>>(Endpoint.PUT.EXCLUSAO_BENEFICIARIO_LIBERAR, { nrSeqRescisao, nrSeqContrato })
      .then((response) => {
        this.customDialog.setVisible(true);
        catchRenderException(() => this.textProtocolo.setValue(`${msg('stipulator.protocolo.colon')} ${response.body ?.response}`));
        this.refreshList();
      }).catch(({ body }) => {
        if (!body) {
          onCatchError();
          return;
        }

        const { response, status } = body;

        if (status === StatusRetorno.WARNING) {
          onCatchError(response);
          return;
        }

        onCatchError();
      });
  }

  private cancelar(nrSeqRescisao: number): void {
    const { nrSeqContrato } = StipulatorContext.getCurrentContract();

    HttpService.put(Endpoint.PUT.EXCLUSAO_BENEFICIARIO_CANCELAR, { nrSeqRescisao, nrSeqContrato })
      .then(() => this.refreshList())
      .catch(({ body }) => {
        if (!body) {
          onCatchError();
          return;
        }

        const { response, status } = body;

        if (status === StatusRetorno.WARNING) {
          onCatchError(response);
          return;
        }

        onCatchError();
      });
  }

  private getRescisao(nrSeqRescisao: number, ieSolicitacaoRescisao: IeSolicitacaoRescisao): void {
    const { nrSeqContrato } = StipulatorContext.getCurrentContract();

    HistoryService.push(Route.BENEF_DETALHE, { nrSeqContrato, nrSeqRescisao, ieSolicitacaoRescisao });
  }

  private getReenvioRescisao(nrSeqRescisao: number, ieSolicitacaoRescisao: IeSolicitacaoRescisao): void {
    const { nrSeqContrato } = StipulatorContext.getCurrentContract();

    HistoryService.push(Route.BENEF_REENVIAR_EXCLUSAO, { nrSeqContrato, nrSeqRescisao, ieSolicitacaoRescisao });
  }

  private refreshList(): void {
    this.fetchConsulta.forceFetch();
  }

  private imprimir(nrSequencia: number, ieSolicitacaoRescisao: string): void {
    const ieTipoRescisao = ieSolicitacaoRescisao === 'S' ? 'SR' : 'RP';
    this.reportService = new ReportService('19', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_SEQUENCIA: nrSequencia, IE_TIPO_RESCISAO: ieTipoRescisao });
  }

}
